import {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import "./DocumentsPage.scss";
import { useDarkModeCheck } from "../DashboardLayout";
import FilterRow from "../../../component/filter-row/FilterRow";
import {
  documentTypeSelectorList,
  SortOrder,
  tableDocumentsHeader,
} from "../../../service/utils/constants";
import SortableTable, {
  SortSettings,
} from "../../../component/sortable-table/SortableTable";
import Container from "../../../component/container/Container";
import { sortTableFunction } from "../../../service/utils/functions";
import {
  GET_CUSTOMERS,
  GET_DOCUMENT_DATA,
} from "../../../service/graphql/query";
import * as Storage from "../../../service/storage";
import {
  CustomerDataEntry,
  SiteDataEntry,
} from "../../../service/utils/interfaces";
import { documentsDataFormat } from "../../../service/utils/parsers";
import { logout } from "../../../service/graphql/utils";

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  // 1. Add new loading state for initial customer/filter data
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const [headerLine, setHeaderLine] = useState<any[]>([]);
  const [content, setContent] = useState<any[][]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState("");
  const [reordering, setReordering] = useState(false);

  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<any[]>([]);
  const [selectedSites, setSelectedSites] = useState<any>();
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<any>();

  const [width, setWidth] = useState(0);

  const [getCustomers, { data: userData }] = useLazyQuery(GET_CUSTOMERS);
  const [getDocuments, { data: documentsData }] =
    useLazyQuery(GET_DOCUMENT_DATA);

  // This effect implements the loading timeout.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    // If loading is still in progress, start a 15-second timeout.
    if (isInitialLoading) {
      timeoutId = setTimeout(() => {
        console.warn("Data loading timed out. Clearing cache and reloading.");
        // Clear the potentially corrupted user data cache
        Storage.USER_DATA.remove().then(() => {
          // Force a full page reload to fetch fresh data
          window.location.reload();
        });
      }, 5000); // 5-second timeout
    }

    // Cleanup function: If the component unmounts or if loading completes
    // successfully, clear the timeout to prevent it from firing.
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitialLoading]); // This effect depends only on the loading state.

  // Add this useEffect to handle page restoration
  useEffect(() => {
    if ((document as any).wasDiscarded) {
      console.warn("DocumentsPage was discarded and restored. Forcing a data refresh.");
      // Clear the potentially stale user data and reload to ensure a clean state.
      Storage.USER_DATA.remove().then(() => {
        window.location.reload();
      });
    }
  }, []); // Empty dependency array ensures this runs only once on mount.

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  const checkUserPermissions = useCallback(
    async (customers: any[]) => {
      const user = customers[0];
      if (user) {
        if (user.category === "Tipping") {
          window.history.replaceState(null, "", "/");
          navigate("/dashboard/home");
          return;
        }
        setCustomerList(customers);
        const filter = await Storage.FILTER_DATA.get();
        const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
        setSelectedCustomer(customers[idx]);
        setReadyToFetch(true);
        setIsInitialLoading(false);
      } else logout(navigate);
    },
    [navigate]
  );

  useEffect(() => {
    setHeaderLine(tableDocumentsHeader);

    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          checkUserPermissions(customerList);
        } else {
          getCustomers({ context: { apiName: "middleware" } });
        }
      })
      .catch(() => {
        getCustomers({ context: { apiName: "middleware" } });
      });

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getCustomers, checkUserPermissions]);

  useEffect(() => {
    if (userData && userData.getCustomers && userData.getCustomers.length > 0) {
      manageCustomersData(userData.getCustomers);
      checkUserPermissions(userData.getCustomers);
    }
  }, [userData, checkUserPermissions]);

  const manageCustomersData = async (customersData: any) => {
    const tempCustomerList: CustomerDataEntry[] = [];
    customersData.forEach((elem: any) => {
      const customerSites: SiteDataEntry[] = [];
      elem.sites?.forEach((site: any) => {
        const siteId = parseInt(site.id);
        customerSites.push({
          value: siteId,
          label: site.customerSite,
        });
      });
      tempCustomerList.push({
        value: elem.id,
        label: elem.customer,
        category: elem.category,
        sites: customerSites,
      });
    });
    Storage.USER_DATA.set(tempCustomerList);
  };

  useEffect(() => {
    if (selectedCustomer) {
      // This ensures the available sites are set first.
      const sites = selectedCustomer.sites;
      setSitesPerCustomer(sites);

      // Now, safely restore the selection from the populated list.
      Storage.FILTER_DATA.get().then((filter) => {
        if (filter.siteIndex) {
          const siteList: any[] = [];
          sites.forEach((site: any, idx: number) => {
            if (filter.siteIndex.includes(idx)) {
              siteList.push(site);
            }
          });
          setSelectedSites(siteList);
        } else {
          setSelectedSites([]); // Explicitly set to empty array if nothing is stored
        }
      });
      setSelectedDocumentTypes([]);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (readyToFetch && selectedSites && selectedDocumentTypes) {
      let siteIdList: any[] = [];
      if (selectedSites.length > 0)
        siteIdList = selectedSites.map((el: any) => el.value);
      else siteIdList = sitesPerCustomer.map((el: any) => el.value);

      let doctypeList: any[] = [];
      if (selectedDocumentTypes.length > 0)
        doctypeList = selectedDocumentTypes.map((el: any) => el.label);
      else
        doctypeList = documentTypeSelectorList.map((doctype) => doctype.label);

      getDocuments({
        variables: {
          input: {
            siteList: siteIdList,
            documentTypeList: doctypeList,
            documentId: "-99",
          },
        },
        context: { apiName: "middleware" },
      });
    }
  }, [
    readyToFetch,
    selectedSites,
    sitesPerCustomer,
    selectedDocumentTypes,
    getDocuments,
  ]);

  useEffect(() => {
    if (documentsData && documentsData.getJobIssueDocList) {
      manageDocumentsData(documentsData);
    }
  }, [documentsData, selectedCustomer]);

  const manageDocumentsData = (documentsData: any) => {
    const parsedDocuments = documentsDataFormat(
      documentsData.getJobIssueDocList.documents
    );
    setContent(parsedDocuments);
  };

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = content.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setContent(reorderedTable);
    }
  }, [sortSettings, reordering, content]);

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  // 4. Implement the loading guard
  if (isInitialLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={"documents-wrapper" + (darkMode ? " dark" : "")}>
      <div className="filter-row row">
        <FilterRow
          width={width}
          customerData={customerList}
          onChangeCustomerData={(val) => {
            resetTable();
            setSelectedCustomer(val);
          }}
          siteData={sitesPerCustomer}
          onChangeSiteData={(val) => {
            resetTable();
            setSelectedSites([...val]);
          }}
          documentTypeData={documentTypeSelectorList}
          onChangeDocumentTypeData={(val) => {
            resetTable();
            setSelectedDocumentTypes([...val]);
          }}
          darkMode={darkMode}
        />
      </div>
      <Container darkTheme={darkMode}>
        <div className="column">
          <SortableTable
            id="document-list"
            width={width}
            header={headerLine}
            data={content}
            standaloneTable
            isAuditTable
            isDocumentTable
            currentSort={sortSettings}
            onSortChanged={changeSortAction}
            currentFilterKey={searchParam}
            onFilterChanged={changeSearchParam}
            rowsPerPage={25}
            darkMode={darkMode}
          />
        </div>
      </Container>
    </div>
  );
};

export default DocumentsPage;
